import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@material-ui/icons';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import IconButton from 'Components/Atoms/IconButton';
import Tabs, { Tab } from 'Components/Atoms/Tabs';
import Typography from 'Components/Atoms/Typography';
import TabCard from 'Components/Molecules/TabCard';
import type { ContextAction } from 'Components/Organisms/GTable';
import MobileModal from 'App/Mobile/Components/MobileModal';
import useVisitWishReservation from 'CustomHooks/useVisitWishReservation';
import FindTable from 'Components/Atoms/Icons/FindTable';
import { conversionOf } from './wishConversion';
import { dateHelper, toEuropeanDate } from 'utils/helper';
import { VisitWish } from './visitWishApi';
import { clock, ExclusiveCell, OFFER_LABELS } from './ExclusiveOffers';
import { PriorityReach } from './wishFilters';
import WishStatus, { period, timeWindow } from './WishStatus';

export const DELIVERY_LABELS: Record<string, string> = {
  pending: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Not delivered',
  unknown: 'Unclear, being checked',
  cancelled: 'Not sent (no longer valid)',
};

const ENDED_LABELS: Record<string, string> = {
  fulfilled: 'Booked',
  withdrawn: 'Withdrawn by guest',
  stopped: 'Stopped by team',
  expired: 'Period over',
};

const TABS: Tab[] = [
  { id: 0, label: 'Details', translation: 'reservations' },
  { id: 1, label: 'Contact', translation: 'reservations' },
  { id: 2, label: 'History', translation: 'reservations' },
];

export type WishDetailsProps = {
  wish: VisitWish;
  restaurantId: string;
  priority: PriorityReach;
  /** The calendar day the team is looking at; a conversion prefers it. */
  selectedDay?: string | null;
  actions: ContextAction<VisitWish>[];
  priorityActions: ContextAction<VisitWish>[];
  /** An action for this wish is still running. */
  busy?: boolean;
  onClose: () => void;
  /** A docked sidebar on wide screens, the mobile modal on a phone. */
  presentation?: 'panel' | 'modal';
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="reservation-field visit-wish-field">
    <dt className="text-3">{label}</dt>
    <dd className="text-3">{children}</dd>
  </div>
);

/** One wish in the reservation-sidebar layout: header, tabs, a scrolling body and fixed actions. */
const WishDetails: React.FC<WishDetailsProps> = ({
  wish,
  restaurantId,
  priority,
  selectedDay = null,
  actions,
  priorityActions,
  busy = false,
  onClose,
  presentation = 'panel',
}) => {
  const { t, i18n } = useTranslation('reservations');
  const [tab, setTab] = useState<number>(0);
  const beginReservation = useVisitWishReservation(restaurantId);
  const day = (time: number) => toEuropeanDate(dateHelper(time));
  const language = i18n?.language ?? 'de';
  const conversion = conversionOf(wish, selectedDay);
  const shownActions = actions.filter((action) => action.showCondition?.(wish) ?? true);
  const shownPriorityActions = priorityActions.filter((action) => action.showCondition?.(wish) ?? true);

  useEffect(() => {
    setTab(0);
  }, [wish.id]);

  const details = (
    <dl className="visit-wish-fields-list">
      <Field label={t('Status')}>
        <WishStatus wish={wish} />
      </Field>
      <Field label={t('Wished date')}>{period(wish)}</Field>
      <Field label={t('Time')}>{timeWindow(wish, t)}</Field>
      <Field label={t('Guests')}>{wish.guests}</Field>
      {wish.minLeadHours ? (
        <Field label={t('Notice')}>{t('at least {{hours}} h notice', { hours: wish.minLeadHours })}</Field>
      ) : null}
      {priority !== 'none' && (
        <Field label={t('Priority')}>
          <span className="visit-wish-priority">
            {wish.priority}
            {shownPriorityActions.map((action) => (
              <IconButton
                key={action.tooltipContent}
                size="small"
                color="subdued"
                aria-label={t(action.tooltipContent ?? '')}
                title={t(action.tooltipContent ?? '')}
                disabled={busy}
                onClick={() => action.onClick(wish)}
              >
                {action.icon}
              </IconButton>
            ))}
          </span>
          {priority === 'some' ? (
            <span className="visit-wish-fact-note">{t('Priority applies only on days whose rule puts priority first.')}</span>
          ) : null}
        </Field>
      )}
      {wish.vipTags?.length ? <Field label={t('VIP')}>{wish.vipTags.join(', ')}</Field> : null}
      {wish.fulfilledBy ? (
        <Field label={t('Result')}>{t('Booked on {{date}}', { date: day(wish.fulfilledBy.at) })}</Field>
      ) : ENDED_LABELS[wish.status] ? (
        <Field label={t('Result')}>{t(ENDED_LABELS[wish.status])}</Field>
      ) : null}
      {wish.exclusive || wish.offers?.length ? (
        <Field label={t('Exclusive')}>
          <ExclusiveCell wish={wish} />
        </Field>
      ) : null}
      {Object.entries(wish.deliveryHolds ?? {}).map(([slot, hold]) => (
        <Field key={slot} label={t('Offer delivery')}>
          {`${slot.split('|')[0] ? toEuropeanDate(slot.split('|')[0]) : ''} ${slot.split('|')[2] ?? ''} · ${
            hold.reason === 'uncertain'
              ? t('delivery unclear – mark exclusive again to offer this slot again')
              : hold.blockedAt
                ? t('not deliverable, stopped – check the contact and mark exclusive again')
                : t('not deliverable, next try after {{time}}', { time: new Date(hold.retryAt ?? 0).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) })
          }`}
        </Field>
      ))}
      <Field label={t('Received')}>{day(wish.createdAt)}</Field>
    </dl>
  );

  const contact = (
    <dl className="visit-wish-fields-list">
      <Field label={t('Name')}>{wish.contact.name}</Field>
      <Field label={t('Email')}><a href={`mailto:${encodeURIComponent(wish.contact.email)}`}>{wish.contact.email}</a></Field>
      {wish.contact.phone ? <Field label={t('Telephone')}><a href={`tel:${wish.contact.phone.replace(/[^+0-9]/g, '')}`}>{wish.contact.phone}</a></Field> : null}
      <Field label={t('Notified by')}>{wish.channels.sms ? t('Email and SMS') : t('Email')}</Field>
      <Field label={t('Confirmed by email')}>
        {wish.confirmedAt ? day(wish.confirmedAt) : t('Waiting for confirmation')}
      </Field>
    </dl>
  );

  const history = (
    <div className="visit-wish-history-tab">
      {wish.offers?.length ? (
        <>
          <Typography variant="text-3" weight="medium" translation="reservations" block>
            Offers
          </Typography>
          <ul className="visit-wish-history">
            {wish.offers.map((offer) => (
              <li key={offer.id}>
                <Typography variant="text-3" block>
                  {t(OFFER_LABELS[offer.status])} · {toEuropeanDate(offer.slot.date)} {offer.slot.time}
                  {offer.tables.length ? ` · ${t('Table')} ${offer.tables.join(', ')}` : ''}
                </Typography>
                <Typography variant="text-4" color="subdued" block>
                  {clock(offer.createdAt, language)}
                </Typography>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <Typography variant="text-3" weight="medium" translation="reservations" block>
        Notification history
      </Typography>
      {wish.alerts.length ? (
        <ul className="visit-wish-history">
          {wish.alerts.map((alert) => {
            const email = alert.delivery.email?.status ?? 'pending';
            return (
              <li key={alert.id}>
                <Typography variant="text-3" block>
                  {alert.slots.map((slot) => `${toEuropeanDate(slot.date)} ${slot.time}`).join(', ')}
                </Typography>
                <Typography variant="text-4" color="subdued" block>
                  {clock(alert.createdAt, language)} · {t('Email')}: {t(DELIVERY_LABELS[email] ?? email)}
                  {alert.delivery.sms
                    ? ` · SMS: ${t(DELIVERY_LABELS[alert.delivery.sms.status] ?? alert.delivery.sms.status)}`
                    : ''}
                </Typography>
              </li>
            );
          })}
        </ul>
      ) : (
        <Typography variant="text-3" color="subdued" translation="reservations" block>
          No notification sent yet.
        </Typography>
      )}
    </div>
  );

  const body = <div className="visit-wish-sidebar-scroll">{tab === 1 ? contact : tab === 2 ? history : details}</div>;

  const footer =
    conversion || shownActions.length ? (
      <Box className="visit-wish-sidebar-actions">
        {conversion && (
          <>
            <Button
              fullWidth
              variant="primary"
              translation="reservations"
              disabled={busy || !conversion.possible}
              endIcon={() => <FindTable style={{ marginLeft: 8 }} />}
              onClick={() => beginReservation(wish, selectedDay ?? undefined)}
            >
              {conversion.label}
            </Button>
            <Typography variant="text-4" color="subdued" block className="visit-wish-sidebar-hint">
              {t(conversion.hint, conversion.values)}
            </Typography>
          </>
        )}
        {shownActions.map((action) => (
          <Button
            key={action.tooltipContent}
            fullWidth
            variant="outlined"
            color={action.tooltipContent === 'Stop request' ? 'error' : 'textPrimary'}
            translation="reservations"
            disabled={busy}
            endIcon={() => <span className="visit-wish-sidebar-action-icon">{action.icon}</span>}
            onClick={() => action.onClick(wish)}
          >
            {action.tooltipContent}
          </Button>
        ))}
      </Box>
    ) : null;

  if (presentation === 'modal') {
    return (
      <MobileModal isOpen title={wish.contact.name} onClose={onClose} padding={0}>
        <div className="visit-wish-sidebar visit-wish-sidebar--modal" data-testid="wish-details">
          <Tabs id="visit-wish-sidebar-tabs" tabs={TABS} value={tab} onChange={(id) => setTab(Number(id))} />
          {body}
          {footer}
        </div>
      </MobileModal>
    );
  }

  return (
    <section className="visit-wish-sidebar" data-testid="wish-details" role="region" aria-label={wish.contact.name}>
      <TabCard
        tabs={TABS}
        id="visit-wish-sidebar-tabs"
        value={tab}
        onChange={(id) => setTab(Number(id))}
        title={wish.contact.name}
        titleCarousel={wish.contact.name.length > 20}
        titleProps={{ style: { maxWidth: 200 } }}
        headerRight={
          <IconButton size="small" color="subdued" aria-label={t('Close')} title={t('Close')} onClick={onClose}>
            <Close />
          </IconButton>
        }
        className="sidebar visit-wish-sidebar-card"
        style={{ height: '100%' }}
        bodyProps={{ padding: 0, className: 'visit-wish-sidebar-body' }}
      >
        {body}
        {footer}
      </TabCard>
    </section>
  );
};

export default WishDetails;
